import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Shield, AlertTriangle, Scale, TrendingDown, Brain, CheckCircle, 
  Rocket, Lock, Target, Clock, ArrowRight, Building2, Users,
  ShieldCheck, Eye, FileCheck, Briefcase, BarChart3,
  UserCheck, FileWarning, Gavel, Ban, TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function SolucaoBrasilCompliancePage() {
  // Schema.org FAQ for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "O que é compliance empresarial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Compliance empresarial é o conjunto de práticas, políticas e mecanismos que garantem que uma empresa atua em conformidade com leis, regulamentos e boas práticas. Inclui canais de escuta, registro de ocorrências e evidências de boa-fé."
        }
      },
      {
        "@type": "Question",
        "name": "Por que pequenas empresas precisam de compliance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pequenas empresas também enfrentam riscos trabalhistas, conflitos internos e responsabilidades legais. Sem compliance estruturado, sócios e gestores ficam pessoalmente expostos em caso de problemas. Compliance protege a empresa e seus dirigentes."
        }
      },
      {
        "@type": "Question",
        "name": "Qual a diferença entre compliance e governança corporativa?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Compliance foca em conformidade com regras e leis. Governança corporativa é o sistema de direção e controle da empresa, incluindo tomada de decisões, prestação de contas e transparência. Ambos são complementares e essenciais para empresas maduras."
        }
      }
    ]
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "OmbudNow",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Plataforma de compliance empresarial e governança corporativa para SMBs brasileiras. Sistema simples para estruturar canais de escuta e registro de ocorrências.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "BRL"
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      <Helmet>
        <title>Compliance Empresarial e Governança Corporativa | OmbudNow</title>
        <meta name="description" content="Plataforma de compliance empresarial e governança corporativa para SMBs. Proteja sua empresa, sócios e gestores com mecanismos de prevenção, canais de escuta e registro estruturado." />
        <meta name="keywords" content="compliance empresarial, governança corporativa, compliance para empresas, compliance PME, programa de compliance, governança empresarial, compliance trabalhista, gestão de riscos" />
        <link rel="canonical" href="https://ombudnow.com/solucaobrasilcompliance" />
        <meta property="og:title" content="Compliance Empresarial e Governança Corporativa | OmbudNow" />
        <meta property="og:description" content="Proteja sua empresa, seus sócios e seu futuro antes que o risco vire problema." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ombudnow.com/solucaobrasilcompliance" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#1a1a2e]/95 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </Link>
            <Link to="/pt">
              <Button className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] font-semibold">
                Implementar compliance
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="px-6 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#22c55e]/20 text-[#22c55e] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              Compliance Empresarial e Governança Corporativa
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Proteja sua empresa, seus sócios e seu futuro antes que o risco vire problema
            </h1>
            
            <p className="text-xl text-[#22c55e] font-medium mb-8">
              OmbudNow — plataforma simples de compliance e governança corporativa para SMBs
            </p>
            
            <p className="text-lg text-slate-300 mb-4 leading-relaxed">
              A maioria das pequenas e médias empresas no Brasil não possui compliance estruturado.
            </p>
            <p className="text-lg text-slate-400 mb-4 leading-relaxed">
              Não por descuido — mas porque acredita que compliance é algo <strong className="text-white">caro, complexo e exclusivo de grandes corporações</strong>.
            </p>
            <p className="text-lg text-slate-300 mb-10 leading-relaxed">
              Na prática, a ausência de compliance é hoje um dos <strong className="text-white">maiores riscos ocultos para SMBs</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pt">
                <Button size="lg" className="bg-[#22c55e] hover:bg-[#22c55e]/90 text-white text-lg px-8 font-semibold h-14 w-full sm:w-auto">
                  <ShieldCheck className="w-5 h-5 mr-2" />
                  Implementar compliance na minha empresa
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 h-14"
                onClick={() => document.getElementById('risco').scrollIntoView({ behavior: 'smooth' })}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Minha empresa está em risco sem saber?
              </Button>
            </div>
          </div>
        </section>

        {/* Risk Section */}
        <section id="risco" className="px-6 py-16 bg-gradient-to-b from-red-950/30 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O risco invisível da falta de compliance empresarial
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-4">
              Compliance não serve apenas para "cumprir leis".
            </p>
            <p className="text-slate-400 mb-8">
              Ele existe para <strong className="text-white">proteger a empresa quando algo dá errado</strong>.
            </p>
            
            <p className="text-slate-300 mb-6">Sem um programa mínimo de compliance e governança corporativa, sua empresa:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: Ban, text: "Não consegue provar boa-fé" },
                { icon: FileWarning, text: "Não possui registros formais de prevenção" },
                { icon: Eye, text: "Não tem mecanismos de escuta e controle" },
                { icon: Gavel, text: "Fica vulnerável a processos, multas e sanções" },
                { icon: UserCheck, text: "Expõe sócios e gestores à responsabilização direta" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-red-500/20">
                  <item.icon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-8">
              <p className="text-white text-lg font-medium mb-2">O problema não é errar.</p>
              <p className="text-slate-300">O problema é <strong className="text-red-400">não conseguir demonstrar que você tentou evitar</strong>.</p>
            </div>
            
            <Link to="/pt/guias/como-ombudnow-fortalece-compliance-empresarial" className="inline-flex items-center gap-2 text-[#22c55e] hover:underline font-medium">
              📘 Entender o que é compliance empresarial na prática
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Common Mistake Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O erro mais comum das SMBs
              </h2>
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10 mb-8">
              <div className="space-y-3 text-slate-400 italic">
                <p>"Nunca tivemos problemas."</p>
                <p>"Somos uma empresa pequena."</p>
                <p>"Isso é coisa de multinacional."</p>
              </div>
            </div>
            
            <p className="text-[#f5a623] font-semibold mb-6">A realidade:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Problemas internos não avisam",
                "O tamanho da empresa não reduz responsabilidade",
                "A ausência de governança pesa contra a empresa em qualquer disputa"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <span className="text-red-400">✗</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-purple-950/50 rounded-2xl p-6 border border-purple-500/30">
              <p className="text-slate-400 mb-4">Quando surge um problema, a pergunta não é:</p>
              <p className="text-slate-500 italic mb-4">"A empresa é pequena?"</p>
              <p className="text-slate-400 mb-2">A pergunta é:</p>
              <p className="text-white text-lg font-semibold">"A empresa tinha mecanismos de prevenção?"</p>
            </div>
          </div>
        </section>

        {/* Legal Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Compliance, governança corporativa e responsabilidade dos sócios
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">
              No Brasil, a avaliação de empresas em processos, auditorias e contratos evoluiu.
            </p>
            
            <p className="text-slate-300 mb-6">Hoje, órgãos reguladores, Judiciário, parceiros e clientes analisam:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: FileCheck, text: "Existência de políticas internas" },
                { icon: Eye, text: "Canais formais de escuta" },
                { icon: BarChart3, text: "Registro e tratamento de ocorrências" },
                { icon: Building2, text: "Estrutura mínima de governança" },
                { icon: ShieldCheck, text: "Evidências de boa-fé" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#0EA5E9]/10 rounded-xl p-4 border border-[#0EA5E9]/30">
                  <item.icon className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-8">
              <p className="text-white text-lg">
                Sem isso, o risco deixa de ser da empresa e passa a ser <strong className="text-red-400">pessoal para sócios e administradores</strong>.
              </p>
            </div>
            
            <Link to="/pt">
              <Button className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white font-semibold">
                <Building2 className="w-5 h-5 mr-2" />
                Estruturar governança corporativa mínima agora
              </Button>
            </Link>
          </div>
        </section>

        {/* Cost Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O custo real da falta de governança corporativa
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">Empresas sem governança sofrem com:</p>
            
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                "Processos trabalhistas evitáveis",
                "Conflitos internos recorrentes",
                "Decisões mal informadas",
                "Perda de contratos B2B",
                "Dificuldade em crescer ou captar parceiros",
                "Exposição reputacional"
              ].map((item, index) => (
                <span key={index} className="bg-red-500/20 text-red-300 px-4 py-2 rounded-full text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
            
            <p className="text-slate-400 mb-8">
              Tudo isso acontece <strong className="text-white">antes de qualquer crise pública</strong>.
            </p>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
              <p className="text-white text-lg font-medium mb-2">Governança não evita problemas.</p>
              <p className="text-slate-300">Ela <strong className="text-[#22c55e]">reduz impacto, prova diligência e protege decisões</strong>.</p>
            </div>
          </div>
        </section>

        {/* Product Section */}
        <section className="px-6 py-16 bg-gradient-to-b from-[#22c55e]/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#22c55e]/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#22c55e]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O que é o OmbudNow no contexto de compliance
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">
              O OmbudNow é uma plataforma de apoio ao <strong className="text-white">compliance empresarial</strong> e à <strong className="text-white">governança corporativa</strong>, criada para SMBs que precisam:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                "Estruturar mecanismos mínimos de prevenção",
                "Criar canais formais de escuta e reporte",
                "Registrar e organizar ocorrências",
                "Demonstrar boa-fé e diligência",
                "Atender exigências legais e contratuais"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <Target className="w-5 h-5 text-[#22c55e]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-300 mb-6">Com o OmbudNow, sua empresa passa a ter:</p>
            
            <div className="bg-white/5 rounded-2xl p-8 border border-[#22c55e]/30 mb-8">
              <div className="space-y-4">
                {[
                  "Infraestrutura básica de compliance",
                  "Canais formais (ouvidoria e reporte)",
                  "Registro estruturado e auditável",
                  "Evidência concreta de governança",
                  "Ferramenta defensável para processos e auditorias"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                    <span className="text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#22c55e] hover:bg-[#22c55e]/90 text-white text-lg px-8 font-semibold">
                <ShieldCheck className="w-5 h-5 mr-2" />
                Ativar plataforma de compliance agora
              </Button>
            </Link>
          </div>
        </section>

        {/* Insurance Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#f5a623]/20 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Compliance não é burocracia. É seguro empresarial.
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
                <h3 className="text-white font-semibold mb-4">Empresas sem compliance:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Reagem tarde</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Gastam mais</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Sofrem mais</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Se explicam depois</li>
                </ul>
              </div>
              
              <div className="bg-green-500/10 rounded-2xl p-6 border border-green-500/30">
                <h3 className="text-white font-semibold mb-4">Empresas com governança mínima:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Se antecipam</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Registram</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Demonstram diligência</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Protegem sócios e gestores</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-[#f5a623]/10 rounded-2xl p-6 border border-[#f5a623]/30">
              <p className="text-white text-lg font-medium mb-2">Compliance não é custo.</p>
              <p className="text-[#f5a623]">É <strong>seguro contra riscos invisíveis</strong>.</p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Rocket className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Como implementar compliance empresarial na sua empresa
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-4">
              Com o OmbudNow, você não precisa de um programa complexo.
            </p>
            <p className="text-slate-400 mb-8">O processo é simples:</p>
            
            <div className="space-y-4 mb-10">
              {[
                { step: "1️⃣", text: "Criar sua conta" },
                { step: "2️⃣", text: "Estruturar canais e registros" },
                { step: "3️⃣", text: "Comunicar internamente" },
                { step: "4️⃣", text: "Usar os dados para gestão e prevenção" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                  <span className="text-2xl">{item.step}</span>
                  <span className="text-white text-lg">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4 text-slate-400 mb-8">
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem departamento jurídico</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem consultorias caras</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem burocracia excessiva</span>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-lg px-8 font-semibold">
                <Rocket className="w-5 h-5 mr-2" />
                Implementar compliance hoje
              </Button>
            </Link>
          </div>
        </section>

        {/* Security Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#22c55e]/20 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-[#22c55e]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Segurança, rastreabilidade e confiança
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Shield, text: "Ambiente seguro" },
                { icon: Lock, text: "Controle de acesso" },
                { icon: FileCheck, text: "Registro organizado" },
                { icon: BarChart3, text: "Histórico auditável" },
                { icon: Eye, text: "Confidencialidade garantida" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <item.icon className="w-5 h-5 text-[#22c55e]" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-400 italic">
              Porque compliance sem registro não existe.
            </p>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Para quem o compliance OmbudNow é ideal
              </h2>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: Building2, text: "Pequenas e médias empresas" },
                { icon: TrendingUp, text: "Startups em crescimento" },
                { icon: Briefcase, text: "Empresas B2B" },
                { icon: Users, text: "Negócios com riscos trabalhistas" },
                { icon: FileCheck, text: "Empresas que precisam fechar contratos maiores" },
                { icon: UserCheck, text: "Sócios que querem reduzir exposição pessoal" }
              ].map((item, index) => (
                <span key={index} className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-medium">
                  <item.icon className="w-4 h-4" />
                  {item.text}
                </span>
              ))}
            </div>
            
            <p className="text-slate-300 text-lg">
              Se sua empresa cresce, <strong className="text-white">compliance deixa de ser opcional</strong>.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 py-20 bg-gradient-to-t from-[#22c55e]/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#f5a623]/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                A pergunta que separa empresas reativas de empresas maduras
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">
              Se amanhã sua empresa for questionada, você consegue provar que:
            </p>
            
            <div className="space-y-3 mb-8">
              {[
                "Possui mecanismos de prevenção?",
                "Oferece canais formais de escuta?",
                "Registra e trata ocorrências?",
                "Atua com boa-fé e governança?"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <span className="text-[#f5a623]">❓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-10">
              <p className="text-white text-lg">
                Se a resposta for <strong className="text-red-400">"não"</strong>, o risco já existe hoje.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Link to="/pt">
                <Button size="lg" className="bg-[#22c55e] hover:bg-[#22c55e]/90 text-white text-xl px-10 font-bold h-16 w-full sm:w-auto">
                  🚨 Estruturar compliance empresarial agora
                </Button>
              </Link>
              <Link to="/pt/guias/como-implementar-canal-denuncias-ouvidoria-smb">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 h-16 w-full sm:w-auto">
                  📊 Avaliar maturidade de governança
                </Button>
              </Link>
            </div>
            
            <div className="text-center bg-[#22c55e]/10 rounded-2xl p-8 border border-[#22c55e]/30">
              <p className="text-xl text-white font-semibold mb-2">
                Compliance empresarial não é para quem quer parecer correto.
              </p>
              <p className="text-[#22c55e] text-lg">
                É para quem quer continuar existindo.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Related Solutions Section */}
      <section className="px-6 py-12 bg-white/[0.02] border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-white mb-6 text-center">Soluções Relacionadas</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/solucaoBrasil" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#f5a623]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-5 h-5 text-[#f5a623]" />
                <span className="text-white font-medium group-hover:text-[#f5a623] transition-colors">Canal de Denúncias</span>
              </div>
              <p className="text-slate-400 text-sm">Proteja sua empresa contra riscos legais e trabalhistas</p>
            </Link>
            <Link to="/solucaobrasilouvidoria" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#0EA5E9]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <Eye className="w-5 h-5 text-[#0EA5E9]" />
                <span className="text-white font-medium group-hover:text-[#0EA5E9] transition-colors">Ouvidoria Corporativa</span>
              </div>
              <p className="text-slate-400 text-sm">Transforme reclamações em gestão e melhoria contínua</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/pt/sobre" className="text-slate-400 hover:text-white transition-colors">Sobre</Link>
              <Link to="/pt/contato" className="text-slate-400 hover:text-white transition-colors">Contato</Link>
              <Link to="/pt/guias" className="text-slate-400 hover:text-white transition-colors">Guias</Link>
              <Link to="/pt/politica-de-privacidade" className="text-slate-400 hover:text-white transition-colors">Privacidade</Link>
              <Link to="/pt/termos-de-uso" className="text-slate-400 hover:text-white transition-colors">Termos</Link>
            </div>
            
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
